import type {
  OfferKind,
  PortalAppearance,
  PortalHostStatus,
  PortalSettings,
  WindowStarts,
} from "@shared/consumer-tvod";
import type { ContentOwnerBrand } from "@shared/content-owner";
import { jsonBody, unwrap } from "./foyer";

/**
 * Every HTTP call the CMS makes for a content provider's purchase portal and
 * its offers (consumer TVOD, `docs/design/consumer-tvod.md` §8). One module
 * on purpose: the offers API belongs to the commerce slice, and keeping the
 * calls here lets the integrator line them up with its routes in one place.
 *
 * - Portal: `src/server/api/contentOwnerPortal.ts` (staff,
 *   `canManageDistribution`).
 * - Offers: the storefront offer shape (`storefrontOfferStore`'s
 *   `StorefrontOffer`) plus the wave-0 `storefront_offers` columns, at
 *   {@link offersBase}. ⚠️ That path is this slice's ASSUMPTION of the staff
 *   route; align it with the commerce slice's routes on merge.
 */

/* ── Portal ────────────────────────────────────────────────────────────── */

export interface PortalDnsRecord {
  type: "CNAME" | "TXT";
  name: string;
  value: string;
}

export interface PortalView {
  ownerId: string;
  ownerName: string;
  ownerSlug: string;
  ownerEnabled: boolean;
  key: string | null;
  /** An app build already signed in with the key: it can no longer change. */
  keyLocked: boolean;
  host: string | null;
  hostStatus: PortalHostStatus | null;
  hostError: string | null;
  hostCheckedAt: string | null;
  enabled: boolean;
  settings: PortalSettings;
  carriedFeedIds: string[];
  packageIds: string[];
  cnameTarget: string;
  /** What the provider's IT must create, CNAME first. */
  dnsRecords: PortalDnsRecord[];
  /** `fake` = this server has no Cloudflare connection for certificates. */
  certificateProvider: "cloudflare" | "fake";
  /** Nenda-owned hosts this server accepts anyway (dev test portals). */
  allowNendaHosts?: string[];
  /** The provider's colours + logo (written through this route's PATCH). */
  brand?: ContentOwnerBrand;
}

export interface PortalPatch {
  key?: string;
  host?: string | null;
  enabled?: boolean;
  settings?: PortalSettings;
  /** `portal_settings.appearance`; a `settings` patch without it keeps it. */
  appearance?: PortalAppearance | null;
  /** The provider's colours + logo. */
  brand?: ContentOwnerBrand;
  carriedFeedIds?: string[];
  packageIds?: string[];
}

const portalUrl = (ownerId: string) =>
  `/api/cms/v1/content-owners/${encodeURIComponent(ownerId)}/portal`;

export const portalApi = {
  get(ownerId: string): Promise<PortalView> {
    return unwrap<PortalView>(fetch(portalUrl(ownerId)));
  },
  update(ownerId: string, patch: PortalPatch): Promise<PortalView> {
    return unwrap<PortalView>(
      fetch(portalUrl(ownerId), jsonBody("PATCH", patch))
    );
  },
  /** Verify the host now. */
  checkNow(ownerId: string): Promise<PortalView> {
    return unwrap<PortalView>(
      fetch(`${portalUrl(ownerId)}/check`, jsonBody("POST", {}))
    );
  },
};

/* ── Offers ────────────────────────────────────────────────────────────── */

/** What the offer editor owns. `published` is a list control, not a field. */
export interface TvodOfferDraft {
  kind: OfferKind;
  title: string;
  description: string;
  /** `single` / `pick_n`: the packages whose titles may be chosen. */
  poolPackageIds: string[];
  /** `fixed_set`: the exact titles. */
  assetIds: string[];
  /** `pick_n`: N. */
  quantity: number | null;
  /** VAT-inclusive, MAJOR units (the storefront's money convention). */
  price: { amount: number; currency: string };
  vatBps: number | null;
  windowStarts: WindowStarts;
  playWindowHours: number | null;
  startWindowDays: number | null;
}

export interface TvodOffer extends TvodOfferDraft {
  id: string;
  contentOwnerId: string;
  published: boolean;
  /** Something has sold: the terms can no longer change. */
  sold: boolean;
  termsFrozenAt: string | null;
  sort: number;
}

/** The staff offers route for one provider. See the module doc. */
export const offersBase = (ownerId: string) =>
  `/api/cms/v1/content-owners/${encodeURIComponent(ownerId)}/offers`;

export const offersApi = {
  list(ownerId: string): Promise<TvodOffer[]> {
    return unwrap<TvodOffer[]>(fetch(offersBase(ownerId)));
  },
  create(
    ownerId: string,
    draft: TvodOfferDraft & { published?: boolean }
  ): Promise<TvodOffer> {
    return unwrap<TvodOffer>(
      fetch(offersBase(ownerId), jsonBody("POST", draft))
    );
  },
  update(
    ownerId: string,
    offerId: string,
    patch: Partial<TvodOfferDraft> & { published?: boolean }
  ): Promise<TvodOffer> {
    return unwrap<TvodOffer>(
      fetch(
        `${offersBase(ownerId)}/${encodeURIComponent(offerId)}`,
        jsonBody("PATCH", patch)
      )
    );
  },
};

/** An offer's terms are frozen once it has sold (§5.1). */
export function offerTermsFrozen(
  offer: Pick<TvodOffer, "sold" | "termsFrozenAt">
): boolean {
  return offer.sold || offer.termsFrozenAt !== null;
}
